import {copyDefinedKeys, parseNumber} from '@exadel/esl/modules/esl-utils/misc';

import type {VideoOverlayConfig} from 'core/video-overlay-types';

interface ParseMediaConfig {
  param: string;
  selectors?: string;
  allowResource?: boolean;
}

type BrightcoveVideoConfig = {type: 'brightcove', id: string, playerId: string, accountId: string};

export class VideoUtils {
  public static readonly SUPPORTED_TYPES = ['https', 'video', 'youtube', 'brightcove', 'bc-audio', 'ibm'];
  public static readonly SUPPORTED_TYPES_SELECTORS = ['youtube', 'brightcove'];
  public static readonly MEDIA_ID_PARAM = 'media-id';
  public static readonly MEDIA_STRATEGY_PARAM = 'media-strategy';
  public static readonly START_TIME_PARAM = 't';
  public static readonly BRIGHTCOVE_DIRECT_URL =
    /^https:\/\/players\.brightcove\.net\/([^/]+)\/(.+)_([^/]+)\/index\.html(?:\?([^#]*))?(?:#.*)?$/;

  public static mediaStrategies = {
    embed: 'embed',
    overlay: 'overlay',
    delegate: 'delegate'
  };

  public static parseDirectBrightcove(param: string): BrightcoveVideoConfig | null {
    const [, accountId, playerId, , query = ''] = this.BRIGHTCOVE_DIRECT_URL.exec(param.trim()) || [];
    const id = new URLSearchParams(query).get('videoId');
    return accountId && playerId && id ? {type: 'brightcove', id, playerId, accountId} : null;
  }

  /** Parse direct media param in format: type:id. Example: youtube:abc123 */
  public static parseDirectParam(param: string, fromSelectors = false): {type: string, id: string} | null {
    const separatorIndex = param.search(/[.:]/);
    if (separatorIndex === -1) return null;
    const type = param.slice(0, separatorIndex).toLowerCase();
    const supportedTypes = fromSelectors ? this.SUPPORTED_TYPES_SELECTORS : this.SUPPORTED_TYPES;
    if (!supportedTypes.includes(type)) return null;
    const id = param.slice(separatorIndex + 1).trim();
    return id ? {id, type} : null;
  }

  /** @return {boolean} if media type is audio */
  public static isAudio(mediaType: string): boolean {
    return (mediaType || '').indexOf('audio') !== -1;
  }

  /** Build media id param. param = resourceUrl | mediaType.mediaId */
  public static buildMediaIdParam(options: VideoOverlayConfig): string {
    const {id, resource, type} = options;
    if (resource) return resource;
    if (type && id) return `${String(type).toLowerCase()}.${id}`;
    return '';
  }

  /** Build media strategy param */
  protected static buildMediaStrategyParam(options: VideoOverlayConfig): string {
    const {strategy} = options;
    return Object.values(this.mediaStrategies).includes(strategy) ? strategy : '';
  }

  /** Parse media entity from the given url part */
  protected static parseMediaEntity(cfg: ParseMediaConfig): VideoOverlayConfig | null {
    const {param, selectors, allowResource = true} = cfg;
    // Compatibility for legacy video overlay format, where video requested trough url AEM selectors
    if (!param && selectors) return this.parseDirectParam(selectors, true);
    if (!param) return null;
    const startTime = this.getURLStartTime();
    if (param.endsWith('.json') && allowResource) return {resource: param, startTime};
    const directBrightcove = this.parseDirectBrightcove(param);
    if (directBrightcove) return {...directBrightcove, startTime};
    const directParam = this.parseDirectParam(param);
    if (directParam) return {...directParam, startTime};
    return null;
  }

  /** Parse media-strategy url param */
  protected static parseMediaStrategy(usp: URLSearchParams = new URLSearchParams(location.search)): string | null {
    return usp.get(this.MEDIA_STRATEGY_PARAM);
  }

  /** Parse media-id url param */
  public static parseMediaId(usp: URLSearchParams = new URLSearchParams(location.search)): string | null {
    return usp.get(this.MEDIA_ID_PARAM);
  }

  /** Parse start time url param */
  protected static getURLStartTime(usp: URLSearchParams = new URLSearchParams(location.search)): number | undefined {
    const value = usp.get(this.START_TIME_PARAM);
    return parseNumber(value);
  }

  /** Build media share link */
  public static buildMediaUrl(options: VideoOverlayConfig): string {
    const url = new URL(window.location.href);
    const id = this.buildMediaIdParam(options);
    const strategy = this.buildMediaStrategyParam(options);

    id && url.searchParams.set(this.MEDIA_ID_PARAM, id);
    options.startTime && url.searchParams.set(this.START_TIME_PARAM, '' + options.startTime);
    if (strategy) {
      url.searchParams.set(this.MEDIA_STRATEGY_PARAM, strategy);
    } else {
      // Fallback to remove initial strategy (no strategy === default overlay strategy)
      url.searchParams.delete(this.MEDIA_STRATEGY_PARAM);
    }
    return url.toString();
  }

  public static applyDefaultOptions(options: VideoOverlayConfig): VideoOverlayConfig {
    options = Object.assign({}, options);
    options.title = options.title || 'Video';
    options.truncateDescription = true;
    options.analyticsId = options.analyticsId || options.id;
    options.shareTitle = options.title;
    options.shareDescription = options.description;
    options.shareLink = options.shareLink || this.buildMediaUrl(options);
    return options;
  }

  /** Merge options objects */
  public static mergeOptions(config: VideoOverlayConfig, source: VideoOverlayConfig): VideoOverlayConfig {
    return {...copyDefinedKeys(config), ...source};
  }

  /** Parse full media config from the url */
  public static getMediaConfig(): VideoOverlayConfig {
    const path = location.pathname;
    const query = new URLSearchParams(location.search);
    const aemSelectors = path.substring(path.indexOf('.') + 1).replace(/\.html$/, '');

    const strategy = this.parseMediaStrategy(query);
    const mediaEntity = this.parseMediaEntity({
      param: VideoUtils.parseMediaId(query),
      selectors: aemSelectors,
      allowResource: query.has(VideoUtils.MEDIA_ID_PARAM)
    });

    return mediaEntity ? {
      strategy,
      ...mediaEntity
    } : null;
  }

  /** Update url with "media" params */
  public static updateUrl(options: VideoOverlayConfig): void {
    const openStateUrl = this.buildMediaUrl(options);
    window.history.replaceState(null, document.title, openStateUrl);
  }

  /** Clear url from "media" params */
  public static clearUrl(): void {
    const url = new URL(window.location.href);
    url.searchParams.delete(this.MEDIA_ID_PARAM);
    url.searchParams.delete(this.MEDIA_STRATEGY_PARAM);
    url.searchParams.delete(this.START_TIME_PARAM);
    window.history.replaceState(null, document.title, url.toString());
  }
}
